import React, { useContext, useEffect, useMemo } from 'react';

import { media } from 'context';
import { APP_BANNER_HEIGHT, HEADER_Z_INDEX, Icon, Layout, Link, Text } from 'ds';
import { TENANT_ADMIN_PAYMENT_METHODS_PATH } from 'routes';
import type { UIState } from 'store/UI';
import { selectNeedsApprovalWorkOrders } from 'store/AIAssistant/selectors';
import { actions as tenancyActions } from 'store/Tenancy';
import { selectFailedPayments, selectPlatformFees } from 'store/Tenancy/selectors';
import { actions } from 'store/UI';
import { selectUIState } from 'store/UI/selectors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectUserV2 } from 'store/v2/User/selectors';
import { isTenantAdminUser } from 'store/v2/User/utils';
import { userCanViewProposal } from 'ux/Tenant/PlatformFeeProposal/utils';
import { getFailedPayments } from 'ux/Tenant/TenantAdmin/Billing/SummariesAndPayments/requests';

type FailedPaymentBannerAttributes = { type: 'failed_payment' };

interface TenantFailedPaymentBannerViewProps {
  isMobile: boolean;
  appBannerType: UIState['appBannerType'];
  bannerAttributes: FailedPaymentBannerAttributes | undefined;
}

function TenantFailedPaymentBannerView({
  isMobile,
  appBannerType,
  bannerAttributes
}: TenantFailedPaymentBannerViewProps) {
  if (appBannerType == null) return null;

  if (bannerAttributes == null) return null;

  if (bannerAttributes.type === 'failed_payment') {
    return (
      <Layout position="fixed" top={0} left={0} right={0} zIndex={HEADER_Z_INDEX} width="100%">
        <Link href={`${TENANT_ADMIN_PAYMENT_METHODS_PATH}`} opacity={false} fullWidth>
          <Layout
            color="red-700"
            height={APP_BANNER_HEIGHT}
            align="center"
            justify="center"
            paddingX={isMobile ? 24 : undefined}
            paddingY={12}
          >
            <Icon name="warning" stroke={2} color="white" size="xs" />
            <Layout marginLeft={8}>
              <Text size="body-xs" semibold color="white">
                Payment failed. Update your billing info to avoid service interruption.
              </Text>
            </Layout>
            <Layout marginLeft={8} display="inline-flex">
              <Icon name="rightChevron" size="xxs" color="white" />
            </Layout>
          </Layout>
        </Link>
      </Layout>
    );
  }

  return null;
}

function TenantFailedPaymentBanner() {
  const { isMobile } = useContext(media);
  const user = useAppSelector(selectUserV2);
  const platformFees = useAppSelector(selectPlatformFees);
  const platformFeeProposal = platformFees?.find(
    pf => !!pf.sent_at && !pf.approved_at && userCanViewProposal(pf, user?.id)
  );
  const isTenantAdmin = !!(user && isTenantAdminUser(user));
  const needsApprovalWorkOrders = useAppSelector(selectNeedsApprovalWorkOrders);
  const failedPayments = useAppSelector(selectFailedPayments);
  const { appBannerType } = useAppSelector(selectUIState);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!user?.tenancy_id) return;

    getFailedPayments({ tenancyId: user.tenancy_id }).then(({ data: nextFailedPayments }) => {
      dispatch(tenancyActions.setFailedPayments(nextFailedPayments));
    });
  }, [dispatch, user?.tenancy_id]);

  const bannerAttributes = useMemo((): FailedPaymentBannerAttributes | undefined => {
    if (!user) return undefined;

    if (
      !isTenantAdmin ||
      failedPayments === null ||
      failedPayments.length === 0 ||
      !platformFees ||
      platformFeeProposal ||
      needsApprovalWorkOrders.length > 0
    ) {
      return undefined;
    }

    return { type: 'failed_payment' };
  }, [user, isTenantAdmin, failedPayments, platformFees, platformFeeProposal, needsApprovalWorkOrders.length]);

  useEffect(() => {
    if (!bannerAttributes) {
      if (!appBannerType && !platformFeeProposal) {
        dispatch(actions.updateUIState({ appBannerType: null }));
      }
    } else {
      dispatch(actions.updateUIState({ appBannerType: bannerAttributes.type }));
    }
  }, [bannerAttributes, appBannerType, dispatch, platformFeeProposal]);

  return (
    <TenantFailedPaymentBannerView
      isMobile={isMobile}
      appBannerType={appBannerType}
      bannerAttributes={bannerAttributes}
    />
  );
}

export default TenantFailedPaymentBanner;
